import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import classnames from 'classnames';
import style from './MeetingInfo.module.scss';
import { AttendState, MeetingConflict, MeetingRecord, Meeting, InventTag, AllMeetingStatus, NewOrderSignStatus } from 'type/meeting';
import { revokeMeeting, setMeetingInfo, cancelMeeting, closeMeeting, changeAttendState, cancelThisRepeatMeeting, cancelRepeatMeeting } from 'model/meeting';
import { useHistory } from 'react-router-dom';
import { Button, Modal, Popover, Dropdown, Menu } from 'antd';
import cooperationSDK from 'lib/cooperationSdk';
import { RedirectType } from 'util/meeting';
import { queryStringify, formatEndTime } from 'util/format';
import moment from 'moment';
import IotModal from '../IotModal';
import tracker from 'lib/tracker';

import { getStaffId } from 'data/storage';

import { signRoom } from 'model/room';
import UnderApprovalTag from 'component/RoomTable/components/UnderApprovalTag';
import Icon from 'component/Icon';
import { MeetingOptionType } from 'type/meetingDetail';

interface IProps {
  from?: string;
  record: MeetingRecord;
  refresh?: any;
}

type StateTag = {
  tag: string;
  clz: string;
};
const TO_BE_CONFIRMED = '新邀请';
const INVITE_WITHDRAW = '已取消';

const InvitedTagMap: { [k: number]: string } = {
  [AttendState.TO_BE_CONFIRMED]: TO_BE_CONFIRMED,
  [AttendState.INVITE_WITHDRAW]: INVITE_WITHDRAW
};

const MeetingInfo: React.FC<IProps> = (props) => {
  const { from, record, refresh } = props;
  const { meeting } = record;

  const history = useHistory();
  const dispatch = useDispatch();

  const [invitingTag, setInvitingTag] = useState('');
  const [isCreator, setIsCreator] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [meetingStateTag, setMeetingStateTag] = useState(null as StateTag | null);

  const beginTime = dayjs(meeting.beginTime);
  const endTime = dayjs(meeting.endTime);
  const staffId = getStaffId();

  const roomInfo = useMemo(() => {
    if (!meeting?.roomOrderList || meeting.roomOrderList?.length === 0) return '线上会议';
    if (meeting?.roomOrderList.length > 1) {
      return `${meeting.roomOrderList[0].address} ...共${meeting.roomOrderList.length}个地点`;
    } else {
      return meeting.roomOrderList[0]?.address;
    }
  }, [record]);

  useEffect(() => {
    if (!record || !record.meeting) return;
    const tag = InvitedTagMap[record.inviteState];
    const isChange = record.changeLogs && record.changeLogs.length > 0;
    // 判断是否有纪要
    const hasSummary = record.summaryState === 1 ? '有纪要' : '';
    const disBand = meeting.status === AllMeetingStatus.CANCEL ? '已取消' : '';

    setInvitingTag(disBand || hasSummary || tag || (isChange && '有变更') || '');
  }, [record]);

  useEffect(() => {
    const status =
      meeting.status === AllMeetingStatus.CANCEL
        ? null
        : meeting.status === AllMeetingStatus.FINISH
        ? { tag: '已结束', clz: style.disabled }
        : meeting.status === AllMeetingStatus.AUDIT_EXPIRED
        ? { tag: '已过期', clz: style.disabled }
        : meeting.status === AllMeetingStatus.FILED
        ? { tag: '已归档', clz: style.disabled }
        : meeting.status === AllMeetingStatus.AUDIT_REVOKE
        ? { tag: '已撤销', clz: style.disabled }
        : meeting.status === AllMeetingStatus.AUDIT_REFUSE
        ? { tag: '审批拒绝', clz: style.disabled }
        : meeting.status === AllMeetingStatus.DURING
        ? { tag: '会议中', clz: style.primary }
        : { tag: '待开始', clz: style.focus };

    setMeetingStateTag(status);
    setIsCreator(staffId === meeting.creatorStaffId);
  }, [meeting]);

  const curTime = moment().valueOf();
  const meetingClz = classnames(
    style.meeting,
    meeting?.status === AllMeetingStatus.DURING
      ? style.begin
      : meeting.status === AllMeetingStatus.FINISH || meeting.status === AllMeetingStatus.FILED || curTime >= meeting?.endTime
      ? style.past
      : ''
  );
  const conflictMeetingClz = classnames(style.meta, style.conflict);

  const isConflict = (meeting.status === AllMeetingStatus.BEFORE || meeting.status === AllMeetingStatus.DURING) && record.conflict === MeetingConflict.YES;

  const checkout = () => {
    // FIX: 跳转前先清除旧数据，防止获取到错误的数据
    dispatch(setMeetingInfo({} as Meeting));

    const query: any = from ? { from } : {};
    if (record.inviteState === AttendState.INVITE_WITHDRAW) {
      query.type = RedirectType.WITHDRAW;
    }
    tracker?.reporteSchOperation('点击进入详情');
    redirect(`/details/${record.meeting.meetingNo}`, queryStringify(query));
  };
  const signMsgColor = meeting.status === AllMeetingStatus.BEFORE || AllMeetingStatus.DURING;
  // 是否是审批会议
  const isApprovalMeeting = Boolean(meeting?.status === AllMeetingStatus.AUDITING);
  const redirect = (path: string, query?: string) => {
    if (cooperationSDK.isCoEnv) {
      return cooperationSDK.createWindow({ url: location.origin + path, minWidth: 1050, minHeight: 700 });
    }

    history.push(`${path}${query || ''}`);
  };

  const closeIotMsg = () => {
    setIsModalVisible(false);
  };

  const handleClickCancel = (e: any, type: MeetingOptionType) => {
    e.stopPropagation();
    Modal.confirm({
      title: '取消预约',
      icon: '',
      content: '取消后将通知各个参会人，且无法恢复，确定要取消吗？',
      okText: '取消预约',
      cancelText: '保留会议',
      onOk: () => _cancelMeeting(type),
      onCancel: () => {
        tracker?.reporteSchOperation('保留会议');
      },
      okType: 'danger',
      centered: true,
      autoFocusButton: null
    });
  };

  const handleClickCloseMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    Modal.confirm({
      title: '提前结束',
      icon: '',
      content: '确定要提前结束会议吗？',
      okText: '提前结束',
      cancelText: '取消',
      onOk: _closeMeeting,
      centered: true,
      autoFocusButton: null
    });
  };

  const handleClickRevokeMeeting = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    Modal.confirm({
      title: '撤销申请',
      icon: '',
      content: '撤销申请后无法恢复，确定要撤销吗？',
      okText: '确定',
      cancelText: '取消',
      onOk: _revokeMeeting,
      centered: true,
      autoFocusButton: null
    });
  };

  const _cancelMeeting = (type: MeetingOptionType) => {
    let time = 1000;
    tracker?.reporteSchOperation('取消会议预约');
    switch (type) {
      case MeetingOptionType.ThisMeeting:
        dispatch(cancelThisRepeatMeeting(meeting.meetingNo));
        break;
      case MeetingOptionType.AllMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meeting.repeatMeetingNo }));
        break;
      case MeetingOptionType.PartMeeting:
        time = 2500;
        dispatch(cancelRepeatMeeting({ repeatMeetingNo: meeting.repeatMeetingNo, startTimeGe: meeting.beginTime }));
        break;
      case MeetingOptionType.SingleMeeting:
        dispatch(cancelMeeting(meeting));
        break;
    }
    setTimeout(() => {
      refresh();
    }, time);
  };

  const _closeMeeting = () => {
    tracker?.reporteSchOperation('提前结束');
    dispatch(closeMeeting(meeting));
    setTimeout(() => {
      refresh();
    }, 1000);
  };
  const _revokeMeeting = () => {
    if (!meeting.meetingNo) return;
    tracker?.reporteSchOperation('撤销申请');
    dispatch(revokeMeeting(meeting.meetingNo));
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const signClick = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    if (meeting.status === AllMeetingStatus.BEFORE || meeting.status === AllMeetingStatus.DURING) {
      // setIsModalVisible(true);
    }
    tracker?.reporteSchOperation('会议室签到');
    dispatch(signRoom(meeting.meetingNo));
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const iconPointStyle = (target: string) => {
    if (target === TO_BE_CONFIRMED) {
      return '#ff6b00';
    } else if (target === INVITE_WITHDRAW) {
      return '#FF0000';
    }
    return '#ff6b00';
  };

  const isShowSingned = () => {
    if (invitingTag === TO_BE_CONFIRMED || invitingTag === INVITE_WITHDRAW) {
      return false;
    }
    return meeting?.meetingSignView?.signStatus === NewOrderSignStatus.FINISH_SIGN;
  };

  const isShowMeetingStateTag = () => {
    return isApprovalMeeting ? false : meetingStateTag;
  };

  const isShowCancelMeeting = () => {
    if (isApprovalMeeting) {
      return false;
    }
    return isCreator && meeting.status === AllMeetingStatus.BEFORE;
  };

  const chgAttenStatus = (toState: AttendState) => {
    dispatch(
      changeAttendState({
        meetingId: meeting.meetingNo,
        state: toState
      })
    );
    setTimeout(() => {
      refresh();
    }, 1000);
  };

  const handleClickInvents = (e: any, inventTarget: InventTag) => {
    e.stopPropagation();
    switch (inventTarget) {
      case InventTag.join:
        chgAttenStatus(AttendState.JOINED);
        break;
      case InventTag.leave:
        chgAttenStatus(AttendState.ASK_FOR_LEAVE);
        break;
      case InventTag.reject:
        chgAttenStatus(AttendState.REFUSED);
        break;
      default:
        break;
    }
  };

  const getInvents = () => (
    <div className={style.invents}>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.join);
        }}
        className={style.invent_item}
      >
        参加
      </div>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.leave);
        }}
        className={style.invent_item}
      >
        请假
      </div>
      <div
        onClick={(e) => {
          handleClickInvents(e, InventTag.reject);
        }}
        className={style.invent_item}
      >
        拒绝
      </div>
    </div>
  );

  const menu = (
    <Menu>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.ThisMeeting)}>取消本次会议</Menu.Item>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.PartMeeting)}>取消本次及后续周期会议</Menu.Item>
      <Menu.Item onClick={(e) => handleClickCancel(e.domEvent, MeetingOptionType.AllMeeting)}>取消所有周期会议</Menu.Item>
    </Menu>
  );

  return (
    meeting && (
      <div className={meetingClz}>
        <div className={style.timeWarp}>
          <div className={style.time}>
            {beginTime.format('HH:mm')}
            <span className={style.dot}></span>
          </div>
        </div>
        <div className={style.infoWarp} onClick={checkout}>
          <div className={style.titleWarp}>
            <div className={style.title}>{meeting.subject}</div>
            {isApprovalMeeting && <UnderApprovalTag className={style.margin_right} />}
            {isShowMeetingStateTag() && <div className={classnames(style.tag, meetingStateTag?.clz)}>{meetingStateTag?.tag}</div>}
            <div className={style.rightPane}>
              <div className={style.buttons}>
                {isApprovalMeeting && (
                  <Button className={style.frontOverButton} onClick={handleClickRevokeMeeting}>
                    撤销申请
                  </Button>
                )}
                {isShowCancelMeeting() &&
                  (meeting.isRepeatMeeting ? (
                    <Dropdown overlay={menu} placement="bottomCenter">
                      <Button
                        className={style.cancelButton}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        取消预约
                      </Button>
                    </Dropdown>
                  ) : (
                    <Button className={style.cancelButton} onClick={(e) => handleClickCancel(e, MeetingOptionType.SingleMeeting)}>
                      取消预约
                    </Button>
                  ))}

                {isCreator && meeting.status === AllMeetingStatus.DURING && (
                  <Button className={style.frontOverButton} onClick={handleClickCloseMeeting}>
                    提前结束
                  </Button>
                )}
                {!isApprovalMeeting && (
                  <>
                    {/* 如果没有接受邀请，则不显示 */}
                    {invitingTag !== TO_BE_CONFIRMED && meeting?.meetingSignView?.isCanSign && (
                      <Button type="primary" onClick={signClick} className={style.sign}>
                        会议室签到
                      </Button>
                    )}
                  </>
                )}
                <div className={style.invitedTag} style={{ color: iconPointStyle(invitingTag) }}>
                  {invitingTag === TO_BE_CONFIRMED && (
                    <>
                      {meeting.status === AllMeetingStatus.FINISH ? (
                        <>
                          <div className={style.icon_point} style={{ background: iconPointStyle(invitingTag) }}></div>
                          <div className={style.text_color} style={{ color: iconPointStyle(invitingTag) }}>
                            {invitingTag}
                          </div>
                        </>
                      ) : (
                        <Popover content={getInvents()} placement="bottom" trigger="click">
                          <div
                            className={style.newInvite}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                          >
                            <div>{TO_BE_CONFIRMED}</div>
                            <Icon size={16} color="#fff" type="iconinput_arrow_down_ic1x" />
                          </div>
                        </Popover>
                      )}
                    </>
                  )}
                  {invitingTag === INVITE_WITHDRAW && (
                    <>
                      <div className={style.icon_point} style={{ background: iconPointStyle(invitingTag) }}></div>
                      {invitingTag}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={isConflict ? conflictMeetingClz : style.meta}>
            {beginTime.format('HH:mm')} - {formatEndTime(endTime.format('HH:mm'))}
            {isConflict && <span>（冲突）</span>}
          </div>
          <div className={style.meta}>
            {roomInfo}
            {isShowSingned() && <span className={style.signed}>会议室已签到</span>}
          </div>
        </div>
        {signMsgColor && <IotModal show={isModalVisible} onClose={closeIotMsg} iotMessage={meeting.iotMessage} />}
      </div>
    )
  );
};

export default MeetingInfo;
